/* Partitioning a page of the catalogue by the source each dataset came from. Pure.
 *
 * The catalogue is one flat list of every harvested table, and on an estate with more than one
 * registered source that list answers "what tables exist" while hiding "which database is this
 * one in" — the question a steward looking at `public.employee` and `public.department` asks
 * first, especially when two sources legitimately hold a table of the same name.
 *
 * **Four labels, not two, and keeping them apart is the whole point of this file.** A group whose
 * name is missing can be missing for reasons that mean entirely different things, and rendering
 * any of them as the raw `connection_id` is the bare-UUID defect this repo has shipped four times:
 *
 * * `named` — we have the name.
 * * `loading` — the connection list is still in flight. Says nothing about the source.
 * * `unreadable` — that request failed. A fact about *us*, not about the source, and the rows
 *   underneath are still correct and still worth reading.
 * * `unlisted` — the read succeeded and this id was not in it. The list route caps at 200 with no
 *   cursor, so on a large estate this is an ordinary consequence of the cap rather than evidence
 *   the connection was deleted, and the copy must not claim the stronger thing.
 */

import type { CatalogRow } from '@/shared/api/generated/catalog';

export type ConnectionLabel =
  | { readonly kind: 'named'; readonly name: string }
  | { readonly kind: 'loading' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'unlisted' };

export interface DatasetGroup {
  readonly connectionId: string;
  readonly label: ConnectionLabel;
  readonly rows: readonly CatalogRow[];
}

/** What the connection-name read is currently doing. Mirrors the three states a query can be in,
 * because each produces a different label above and the caller is the only one that knows which. */
export type NameLookupStatus = 'loading' | 'unreadable' | 'ready';

export interface NameLookup {
  readonly status: NameLookupStatus;
  /** Ids to names. Empty while loading or unreadable — a caller must not read emptiness as
   * "no connections exist", which is why `status` is carried separately rather than inferred. */
  readonly names: ReadonlyMap<string, string>;
}

function labelFor(connectionId: string, lookup: NameLookup): ConnectionLabel {
  const name = lookup.names.get(connectionId);
  if (name !== undefined) return { kind: 'named', name };
  if (lookup.status === 'loading') return { kind: 'loading' };
  if (lookup.status === 'unreadable') return { kind: 'unreadable' };
  return { kind: 'unlisted' };
}

/** Groups one page's rows by their connection, **preserving the order the server returned them in**.
 *
 * First-appearance order rather than alphabetical by name, and that is deliberate: the listing is
 * sorted `created_at DESC` and paged on a keyset over that sort, so re-ordering the groups would
 * make the page disagree with the sequence the pager walks — page 2 could open with a group that
 * sorted first on page 1. It also means grouping needs no name to decide the order, so the page
 * does not visibly re-arrange itself when the connection list resolves.
 *
 * **Groups describe this page only.** A connection's datasets can span pages, so the same group can
 * appear on several — which is why the header links to that connection's own screen, where the
 * listing is scoped server-side and complete.
 */
export function groupByConnection(
  rows: readonly CatalogRow[],
  lookup: NameLookup,
): readonly DatasetGroup[] {
  const order: string[] = [];
  const byConnection = new Map<string, CatalogRow[]>();

  for (const row of rows) {
    const existing = byConnection.get(row.connection_id);
    if (existing === undefined) {
      order.push(row.connection_id);
      byConnection.set(row.connection_id, [row]);
    } else {
      existing.push(row);
    }
  }

  return order.map((connectionId) => ({
    connectionId,
    label: labelFor(connectionId, lookup),
    /* Non-null by construction: every id in `order` was put there alongside its array. The fallback
     * exists because `Map.get` is typed as possibly-undefined and an assertion here would be a
     * claim the type checker cannot see, which is the shape this codebase refuses elsewhere. */
    rows: byConnection.get(connectionId) ?? [],
  }));
}
